import React, { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { AlertCircle } from 'lucide-react';

interface ReviewActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  action: 'approve' | 'reject' | 'delete';
  reviewId: number;
  loading?: boolean;
}

export const ReviewActionModal: React.FC<ReviewActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  reviewId,
  loading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (action === 'reject' && !reason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setError('');
    onConfirm(reason.trim() || undefined);
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  const getTitle = () => {
    switch (action) {
      case 'approve':
        return 'Approve Review';
      case 'reject':
        return 'Reject Review';
      case 'delete':
        return 'Delete Review';
    }
  };

  const getMessage = () => {
    switch (action) {
      case 'approve':
        return 'Are you sure you want to approve this review? It will be visible to all users.';
      case 'reject':
        return 'Please provide a reason for rejecting this review. The review will be hidden from public view.';
      case 'delete':
        return 'Are you sure you want to permanently delete this review? This action cannot be undone.';
    }
  };

  const getConfirmButtonText = () => {
    switch (action) {
      case 'approve':
        return 'Approve';
      case 'reject':
        return 'Reject';
      case 'delete':
        return 'Delete';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()}>
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
          <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">{getMessage()}</p>
        </div>

        <div className="text-sm text-gray-600">
          <span className="font-medium">Review ID:</span> {reviewId}
        </div>

        {action === 'reject' && (
          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
              Rejection Reason *
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError('');
              }}
              placeholder="Enter the reason for rejecting this review..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
              disabled={loading}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant={action === 'delete' ? 'secondary' : 'primary'}
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1"
          >
            {loading ? 'Processing...' : getConfirmButtonText()}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
